<template>
  <form class="catalog-form" :class="{ 'catalog-table-form': !isSupplierTemplate, 'supplier-form': isSupplierTemplate }" ref="formElement">
    <template v-for="block in orderedBlocks" :key="block.id">
      <ServicePricingFields
        v-if="block.type === 'row' && block.row.layout === 'service-pricing'"
        :fields="block.row.fields"
        :model-value="model"
        :disabled="disabled"
        @update-field="updateField"
      />
      <div
        v-else-if="block.type === 'row'"
        class="form-row"
        :class="rowClassObject(block.row)"
      >
        <div
          v-for="field in block.row.fields"
          :key="field.key"
          class="form-field"
          :class="fieldClassObject(block.row)"
        >
          <label v-if="!field.hideLabel" :for="field.key">{{ field.label }}</label>
          <SupplierPhoneInput
            v-if="isSupplierPhoneField(field)"
            :id="field.key"
            :model-value="model[field.key] || ''"
            :required="field.required"
            :disabled="disabled"
            :readonly="fieldIsReadonly(field)"
            :placeholder="field.placeholder"
            :mobile-status="supplierMobileStatus"
            :error="supplierPhoneValidationMessage"
            @update:model-value="updateField(field.key, $event)"
          />
          <template v-else-if="['text', 'email', 'number'].includes(field.type)">
            <input
              :id="field.key"
              :type="field.type === 'text' ? 'text' : field.type"
              :step="field.type === 'number' ? '0.01' : undefined"
              :placeholder="field.placeholder"
              :required="field.required"
              :disabled="disabled"
              :readonly="fieldIsReadonly(field)"
              :value="model[field.key]"
              :class="{ 'field-input-invalid': emailFieldError(field) }"
              :aria-invalid="emailFieldError(field) ? 'true' : undefined"
              :aria-describedby="emailFieldError(field) ? `${field.key}-error` : undefined"
              @focus="focusedFieldKey = field.key"
              @blur="focusedFieldKey = ''"
              @input="updateField(field.key, $event.target.value)"
            />
            <p v-if="emailFieldError(field)" :id="`${field.key}-error`" class="field-error">
              {{ emailFieldError(field) }}
            </p>
          </template>

          <div v-else-if="isEngagementTypeField(field)" class="engagement-type-field">
            <CatalogSearchInput
              :input-id="field.key"
              :model-value="resolveSelectValue(field)"
              :options="resolveEngagementOptions(field)"
              :placeholder="field.label"
              :disabled="disabled || fieldIsReadonly(field)"
              selection-only
              use-fixed
              @update:model-value="updateField(field.key, $event)"
            />
            <AppointmentIcon name="chevron-down" :size="14" class="engagement-type-chevron" />
          </div>

          <select
            v-else-if="field.type === 'select'"
            :id="field.key"
            :required="field.required"
            :disabled="disabled || fieldIsReadonly(field)"
            :value="resolveSelectValue(field)"
            @change="updateField(field.key, $event.target.value)"
          >
            <option value="">{{ field.placeholder || 'Select an option' }}</option>
            <option
              v-for="option in resolveFieldOptions(field)"
              :key="option.value"
              :value="option.value"
            >
              {{ option.label }}
            </option>
          </select>

          <textarea
            v-else-if="field.type === 'textarea'"
            v-textarea-autosize
            rows="1"
            :id="field.key"
            :placeholder="field.placeholder"
            :required="field.required"
            :disabled="disabled"
            :readonly="fieldIsReadonly(field)"
            :value="model[field.key]"
            @input="updateField(field.key, $event.target.value)"
          />

          <div
            v-else-if="isRightToWorkDateField(field, block.row)"
            class="right-to-work-date-picker"
          >
            <DateRangePicker
              :id="field.key"
              :key="field.key"
              mode="single"
              :use-fixed="fixedDatePickers"
              manual-input
              manual-input-placeholder="DD/MM/YYYY"
              :dateFrom="resolveDateFieldValue(field)"
              :placeholder="field.placeholder || 'Select date'"
              :disabled="disabled || fieldIsReadonly(field)"
              @update="({ dateFrom }) => updateField(field.key, dateFrom)"
            />
          </div>

          <input
            v-else-if="field.type === 'date'"
            :id="field.key"
            type="date"
            :required="field.required"
            :disabled="disabled || fieldIsReadonly(field)"
            :value="resolveDateFieldValue(field)"
            @input="updateField(field.key, $event.target.value)"
          />

          <div v-else-if="field.type === 'computed-text'" class="computed-field">
            {{ resolveComputedText(field) || '—' }}
          </div>

          <div v-else-if="field.type === 'checkbox-table'" class="checkbox-table-field">
            <table
              v-if="resolveFieldOptions(field).length"
              class="checkbox-table"
              :aria-label="field.label"
            >
              <thead>
                <tr>
                  <th>{{ resolveCheckboxTablePrimaryColumnLabel(field) }}</th>
                  <th class="checkbox-cell">Seen</th>
                  <th class="checkbox-cell">Copy</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="option in resolveFieldOptions(field)"
                  :key="resolveCheckboxTableOptionKey(field, option)"
                  :class="{ 'checkbox-table-group-row': isCheckboxTableGroupOption(option) }"
                >
                  <template v-if="isCheckboxTableGroupOption(option)">
                    <th colspan="3" scope="rowgroup">{{ option.label }}</th>
                  </template>
                  <template v-else>
                    <td>{{ option.label }}</td>
                    <td class="checkbox-cell">
                      <input
                        :id="`${field.key}-${option.value}-seen`"
                        type="checkbox"
                        :disabled="disabled || fieldIsReadonly(field)"
                        :checked="Boolean(getCheckboxTableOption(field.key, option.value).seen)"
                        @change="updateCheckboxTableField(field.key, option.value, 'seen', $event.target.checked)"
                      />
                    </td>
                    <td class="checkbox-cell">
                      <input
                        :id="`${field.key}-${option.value}-copied`"
                        type="checkbox"
                        :disabled="disabled || fieldIsReadonly(field)"
                        :checked="Boolean(getCheckboxTableOption(field.key, option.value).copied)"
                        @change="updateCheckboxTableField(field.key, option.value, 'copied', $event.target.checked)"
                      />
                    </td>
                  </template>
                </tr>
              </tbody>
            </table>
            <p v-else class="field-hint">
              No checklist options available.
            </p>
          </div>

          <AttachmentManager
            v-else-if="field.type === 'attachments'"
            :id="field.key"
            v-model="model[field.key]"
            :disabled="disabled"
          />

          <CatalogSearchInput
            v-else-if="field.type === 'client-select'"
            :input-id="field.key"
            :modelValue="field.multiple ? (model[field.key] || []) : (model[field.key] || null)"
            @update:modelValue="(val) =>
              updateField(field.key, field.multiple ? (Array.isArray(val) ? val : []) : (val || ''))
            "
            :options="field.options || []"
            :placeholder="field.placeholder || (field.multiple ? 'Select clients' : 'Select client')"
            :disabled="disabled"
            :multiple="field.multiple"
            :stacked-chips="field.stackedChips"
            :class="{ 'catalog-multi-select': field.multiple }"
            use-fixed
          />

          <div v-else-if="field.type === 'service-groups'" class="relation-field">
            <CatalogSearchInput
              :input-id="field.key"
              :modelValue="relationPickerValues[field.key] || null"
              @update:modelValue="(val) => handleServiceGroupSelect(field.key, val)"
              :options="field.options || []"
              :placeholder="field.placeholder || 'Select service groups'"
              :disabled="disabled"
              use-fixed
            />
            <div v-if="selectedServiceGroups(field).length" class="relation-tags">
              <span
                v-for="group in selectedServiceGroups(field)"
                :key="group.id"
                class="relation-chip"
              >
                <span class="chip-label">{{ group.name }}</span>
                <button
                  type="button"
                  class="chip-remove"
                  :disabled="disabled"
                  @click="removeServiceGroup(field.key, group.id)"
                  aria-label="Remove service group"
                >
                  ×
                </button>
              </span>
            </div>
          </div>

          <div v-else class="unsupported">
            Unsupported field type: {{ field.type }}
          </div>
        </div>
      </div>

      <section v-else class="collapsible-section" :class="blockClassObject(block)">
        <button
          type="button"
          class="collapsible-toggle"
          :disabled="disabled"
          :aria-expanded="isSectionOpen(block.sectionId)"
          :aria-controls="`${block.id}-body`"
          @click="toggleSection(block.sectionId)"
        >
          <span class="collapsible-label">{{ block.label }}</span>
          <BsChevronDown class="collapsible-indicator" :class="{ open: isSectionOpen(block.sectionId) }" />
        </button>

        <div
          v-if="isSectionOpen(block.sectionId)"
          :id="`${block.id}-body`"
          class="collapsible-content"
        >
          <div class="collapsible-body">
            <slot :name="`section-start-${block.sectionId}`" />
            <div
              v-for="row in block.rows"
              :key="row.id"
              class="form-row"
              :class="rowClassObject(row)"
              :role="row.subsectionLabel ? 'group' : undefined"
              :aria-labelledby="row.subsectionLabel ? `${row.id}-subsection-title` : undefined"
              :data-subsection="row.subsection || undefined"
            >
              <div v-if="row.subsectionLabel" class="form-subsection-header">
                <h3
                  :id="`${row.id}-subsection-title`"
                  class="form-subsection-title"
                >
                  {{ row.subsectionLabel }}
                </h3>
              </div>
              <div
                v-for="field in row.fields"
                :key="field.key"
                class="form-field"
                :class="fieldClassObject(row)"
              >
                <label v-if="!field.hideLabel" :for="field.key">{{ field.label }}</label>
                <SupplierPhoneInput
                  v-if="isSupplierPhoneField(field)"
                  :id="field.key"
                  :model-value="model[field.key] || ''"
                  :required="field.required"
                  :disabled="disabled"
                  :readonly="fieldIsReadonly(field)"
                  :placeholder="field.placeholder"
                  :mobile-status="supplierMobileStatus"
                  :error="supplierPhoneValidationMessage"
                  @update:model-value="updateField(field.key, $event)"
                />
                <template v-else-if="['text', 'email', 'number'].includes(field.type)">
                  <input
                    :id="field.key"
                    :type="field.type === 'text' ? 'text' : field.type"
                    :step="field.type === 'number' ? '0.01' : undefined"
                    :placeholder="field.placeholder"
                    :required="field.required"
                    :disabled="disabled"
                    :readonly="fieldIsReadonly(field)"
                    :value="model[field.key]"
                    :class="{ 'field-input-invalid': emailFieldError(field) }"
                    :aria-invalid="emailFieldError(field) ? 'true' : undefined"
                    :aria-describedby="emailFieldError(field) ? `${field.key}-error` : undefined"
                    @focus="focusedFieldKey = field.key"
                    @blur="focusedFieldKey = ''"
                    @input="updateField(field.key, $event.target.value)"
                  />
                  <p v-if="emailFieldError(field)" :id="`${field.key}-error`" class="field-error">
                    {{ emailFieldError(field) }}
                  </p>
                </template>

                <div v-else-if="isEngagementTypeField(field)" class="engagement-type-field">
                  <CatalogSearchInput
                    :input-id="field.key"
                    :model-value="resolveSelectValue(field)"
                    :options="resolveEngagementOptions(field)"
                    :placeholder="field.label"
                    :disabled="disabled || fieldIsReadonly(field)"
                    selection-only
                    use-fixed
                    @update:model-value="updateField(field.key, $event)"
                  />
                  <AppointmentIcon name="chevron-down" :size="14" class="engagement-type-chevron" />
                </div>

                <select
                  v-else-if="field.type === 'select'"
                  :id="field.key"
                  :required="field.required"
                  :disabled="disabled || fieldIsReadonly(field)"
                  :value="resolveSelectValue(field)"
                  @change="updateField(field.key, $event.target.value)"
                >
                  <option value="">{{ field.placeholder || 'Select an option' }}</option>
                  <option
                    v-for="option in resolveFieldOptions(field)"
                    :key="option.value"
                    :value="option.value"
                  >
                    {{ option.label }}
                  </option>
                </select>

                <textarea
                  v-else-if="field.type === 'textarea'"
                  v-textarea-autosize
                  rows="1"
                  :id="field.key"
                  :placeholder="field.placeholder"
                  :required="field.required"
                  :disabled="disabled"
                  :readonly="fieldIsReadonly(field)"
                  :value="model[field.key]"
                  @input="updateField(field.key, $event.target.value)"
                />

                <div
                  v-else-if="isRightToWorkDateField(field, row)"
                  class="right-to-work-date-picker"
                >
                  <DateRangePicker
                    :id="field.key"
                    :key="field.key"
                    mode="single"
              :use-fixed="fixedDatePickers"
                    manual-input
                    manual-input-placeholder="DD/MM/YYYY"
                    :dateFrom="resolveDateFieldValue(field)"
                    :placeholder="field.placeholder || 'Select date'"
                    :disabled="disabled || fieldIsReadonly(field)"
                    @update="({ dateFrom }) => updateField(field.key, dateFrom)"
                  />
                </div>

                <input
                  v-else-if="field.type === 'date'"
                  :id="field.key"
                  type="date"
                  :required="field.required"
                  :disabled="disabled || fieldIsReadonly(field)"
                  :value="resolveDateFieldValue(field)"
                  @input="updateField(field.key, $event.target.value)"
                />

                <div v-else-if="field.type === 'computed-text'" class="computed-field">
                  {{ resolveComputedText(field) || '—' }}
                </div>

                <div v-else-if="field.type === 'checkbox-table'" class="checkbox-table-field">
                  <table
                    v-if="resolveFieldOptions(field).length"
                    class="checkbox-table"
                    :aria-label="field.label"
                  >
                    <thead>
                      <tr>
                        <th>{{ resolveCheckboxTablePrimaryColumnLabel(field) }}</th>
                        <th class="checkbox-cell">Seen</th>
                        <th class="checkbox-cell">Copy</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr
                        v-for="option in resolveFieldOptions(field)"
                        :key="resolveCheckboxTableOptionKey(field, option)"
                        :class="{ 'checkbox-table-group-row': isCheckboxTableGroupOption(option) }"
                      >
                        <template v-if="isCheckboxTableGroupOption(option)">
                          <th colspan="3" scope="rowgroup">{{ option.label }}</th>
                        </template>
                        <template v-else>
                          <td>{{ option.label }}</td>
                          <td class="checkbox-cell">
                            <input
                              :id="`${field.key}-${option.value}-seen`"
                              type="checkbox"
                              :disabled="disabled || fieldIsReadonly(field)"
                              :checked="Boolean(getCheckboxTableOption(field.key, option.value).seen)"
                              @change="updateCheckboxTableField(field.key, option.value, 'seen', $event.target.checked)"
                            />
                          </td>
                          <td class="checkbox-cell">
                            <input
                              :id="`${field.key}-${option.value}-copied`"
                              type="checkbox"
                              :disabled="disabled || fieldIsReadonly(field)"
                              :checked="Boolean(getCheckboxTableOption(field.key, option.value).copied)"
                              @change="updateCheckboxTableField(field.key, option.value, 'copied', $event.target.checked)"
                            />
                          </td>
                        </template>
                      </tr>
                    </tbody>
                  </table>
                  <p v-else class="field-hint">
                    No checklist options available.
                  </p>
                </div>

                <AttachmentManager
                  v-else-if="field.type === 'attachments'"
                  :id="field.key"
                  v-model="model[field.key]"
                  :disabled="disabled"
                />

                <CatalogSearchInput
                  v-else-if="field.type === 'client-select'"
                  :input-id="field.key"
                  :modelValue="field.multiple ? (model[field.key] || []) : (model[field.key] || null)"
                  @update:modelValue="(val) =>
                    updateField(field.key, field.multiple ? (Array.isArray(val) ? val : []) : (val || ''))
                  "
                  :options="field.options || []"
                  :placeholder="field.placeholder || (field.multiple ? 'Select clients' : 'Select client')"
                  :disabled="disabled"
                  :multiple="field.multiple"
                  :stacked-chips="field.stackedChips"
                  :class="{ 'catalog-multi-select': field.multiple }"
                  use-fixed
                />

                <div v-else-if="field.type === 'service-groups'" class="relation-field">
                  <CatalogSearchInput
                    :input-id="field.key"
                    :modelValue="relationPickerValues[field.key] || null"
                    @update:modelValue="(val) => handleServiceGroupSelect(field.key, val)"
                    :options="field.options || []"
                    :placeholder="field.placeholder || 'Select service groups'"
                    :disabled="disabled"
                    use-fixed
                  />
                  <div v-if="selectedServiceGroups(field).length" class="relation-tags">
                    <span
                      v-for="group in selectedServiceGroups(field)"
                      :key="group.id"
                      class="relation-chip"
                    >
                      <span class="chip-label">{{ group.name }}</span>
                      <button
                        type="button"
                        class="chip-remove"
                        :disabled="disabled"
                        @click="removeServiceGroup(field.key, group.id)"
                        aria-label="Remove service group"
                      >
                        ×
                      </button>
                    </span>
                  </div>
                </div>

                <div v-else class="unsupported">
                  Unsupported field type: {{ field.type }}
                </div>

                <p v-if="resolveFieldHint(field)" class="field-hint">
                  {{ resolveFieldHint(field) }}
                </p>
              </div>
            </div>
            <slot :name="`section-end-${block.sectionId}`" />
          </div>
        </div>
      </section>
    </template>
  </form>
</template>

<script setup>
import { computed, nextTick, ref, watch } from 'vue';
import { BsChevronDown } from '@kalimahapps/vue-icons';
import AttachmentManager from './AttachmentManager.vue';
import ServicePricingFields from './ServicePricingFields.vue';
import SupplierPhoneInput from './SupplierPhoneInput.vue';
import DateRangePicker from '@/components/DateRangePicker.vue';
import CatalogSearchInput from '@/components/CatalogSearchInput.vue';
import AppointmentIcon from '@/components/icons/AppointmentIcon.vue';
import { vTextareaAutosize } from '@/directives/textareaAutosize';
import { isUnfinishedEmail } from '@/utils/email';

const props = defineProps({
  fixedDatePickers: { type: Boolean, default: false },
  template: { type: Object, required: true },
  modelValue: {
    type: Object,
    default: () => ({}),
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  supplierMobileStatus: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['update:modelValue']);

const formElement = ref(null);
const model = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value),
});

const relationPickerValues = ref({});
const normalizePhoneInput = (value) =>
  String(value || '')
    .trim()
    .replace(/[\s()-]/g, '');
const RIGHT_TO_WORK_SECTION_ID = 'right-to-work-uk';
const normalizeRightToWorkListTypeValue = (value) => {
  const normalized = String(value ?? '').trim().toLowerCase();
  if (normalized === 'list_a' || normalized === 'list_b') return normalized;
  return String(value ?? '');
};
const isSupplierTemplate = computed(() => props.template?.key === 'suppliers');
const collapsedSections = ref({});

const matchesVisibilityRule = (rule) => {
  if (!rule) return true;
  if (typeof rule === 'function') return rule(model.value);
  const currentValue = rule.field === 'rightToWorkListType'
    ? normalizeRightToWorkListTypeValue(model.value?.[rule.field])
    : rule.field === 'rightToWorkEngagementType'
      ? String(model.value?.[rule.field] ?? '').trim().toLowerCase()
      : model.value?.[rule.field];
  if (Array.isArray(rule.equalsAny)) {
    return rule.equalsAny.some((value) => String(currentValue ?? '') === String(value));
  }
  if (rule.equals !== undefined) {
    return String(currentValue ?? '') === String(rule.equals);
  }
  if (rule.notEquals !== undefined) {
    return String(currentValue ?? '') !== String(rule.notEquals);
  }
  return Boolean(currentValue);
};

const fieldIsVisible = (field) => matchesVisibilityRule(field?.visibleWhen);
const rowIsVisible = (row) => matchesVisibilityRule(row?.visibleWhen);
const visibleFieldsForRow = (row) => (Array.isArray(row?.fields) ? row.fields : [])
  .filter((field) => fieldIsVisible(field));
const isRightToWorkSection = (sectionId = '') =>
  String(sectionId || '').trim() === RIGHT_TO_WORK_SECTION_ID;
const INLINE_LAYOUT_SECTIONS = new Set([RIGHT_TO_WORK_SECTION_ID, 'general-info']);
const isInlineSection = (sectionId = '') =>
  INLINE_LAYOUT_SECTIONS.has(String(sectionId || '').trim());
const isGeneralInfoTable = (sectionId) =>
  isSupplierTemplate.value && sectionId === 'general-info';
const normalizeSubsectionClass = (value) => String(value ?? '')
  .trim()
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, '-');
const rowClassObject = (row = {}) => {
  const subsectionClass = normalizeSubsectionClass(row?.subsection);
  const isRightToWorkSubsection = isRightToWorkSection(row?.section) && Boolean(subsectionClass);
  return {
    columns: Array.isArray(row?.fields) && row.fields.length > 1,
    'catalog-info-table': !isSupplierTemplate.value,
    'supplier-table-row': isSupplierTemplate.value && row?.layout === 'supplier-table',
    'right-to-work-row': isInlineSection(row?.section) && !isGeneralInfoTable(row?.section),
    'right-to-work-subsection': isRightToWorkSubsection,
    [`right-to-work-subsection--${subsectionClass}`]: isRightToWorkSubsection,
  };
};
const fieldClassObject = (row = {}) => ({
  'right-to-work-field': isInlineSection(row?.section) && !isGeneralInfoTable(row?.section),
});
const blockClassObject = (block = {}) => ({
  'right-to-work-section': isRightToWorkSection(block?.sectionId),
  'general-info-table': isGeneralInfoTable(block?.sectionId),
  'catalog-info-table': !isSupplierTemplate.value || isInlineSection(block?.sectionId),
  'additional-info-section': isSupplierTemplate.value && block?.sectionId === 'additional-info',
});
const isRightToWorkDateField = (field = {}, row = {}) => (
  field?.type === 'date' && isInlineSection(row?.section)
);

const orderedBlocks = computed(() => {
  const rows = (Array.isArray(props.template?.rows) ? props.template.rows : [])
    .filter((row) => rowIsVisible(row))
    .map((row) => ({
      ...row,
      fields: visibleFieldsForRow(row),
    }))
    .filter((row) => row.fields.length > 0);
  if (!rows.length) return [];

  const sections = new Map();
  rows.forEach((row) => {
    const sectionId = String(row?.section || '').trim();
    if (!sectionId) return;
    if (!sections.has(sectionId)) {
      sections.set(sectionId, {
        id: `section-${sectionId}`,
        type: 'section',
        sectionId,
        label: row?.sectionLabel || 'Section',
        rows: [],
      });
    }
    sections.get(sectionId).rows.push(row);
  });

  const seenSections = new Set();
  const blocks = [];
  rows.forEach((row) => {
    const sectionId = String(row?.section || '').trim();
    if (!sectionId) {
      blocks.push({ id: `row-${row.id}`, type: 'row', row });
      return;
    }
    if (seenSections.has(sectionId)) return;
    seenSections.add(sectionId);
    const sectionBlock = sections.get(sectionId);
    if (sectionBlock) {
      blocks.push(sectionBlock);
    }
  });

  return blocks;
});

const isSectionOpen = (sectionId) => !collapsedSections.value[sectionId];

const toggleSection = (sectionId) => {
  collapsedSections.value = {
    ...collapsedSections.value,
    [sectionId]: !collapsedSections.value[sectionId],
  };
};

const normalizeRightToWorkListChange = (currentModel, nextListType) => {
  const previousListType = normalizeRightToWorkListTypeValue(currentModel?.rightToWorkListType);
  const normalizedNextListType = normalizeRightToWorkListTypeValue(nextListType);
  const next = {
    ...currentModel,
    rightToWorkListType: nextListType,
  };

  if (previousListType === normalizedNextListType) {
    return next;
  }

  next.rightToWorkFollowUpBasis = '';
  next.rightToWorkPvnValidUntil = '';
  next.rightToWorkExpiryDate = '';
  next.rightToWorkFollowUpCompletedDate = '';
  next.rightToWorkFollowUpConductedBy = '';

  return next;
};

const updateField = (key, value) => {
  if (key === 'rightToWorkEngagementType' && value !== model.value[key]) {
    model.value = {
      ...model.value,
      [key]: value,
      engagementEffectiveFrom: value === 'employee' ? new Date().toISOString().slice(0, 10) : null,
    };
    return;
  }
  if (!key) return;
  if (key === 'rightToWorkListType') {
    model.value = normalizeRightToWorkListChange(model.value, value);
    return;
  }
  model.value = {
    ...model.value,
    [key]: value,
  };
};

const normalizeSelectOptionValue = (value) => String(value ?? '').trim();

const withLegacySelectOption = (field, options) => {
  if (field?.type !== 'select' || !field?.key) return options;
  const currentValue = model.value?.[field.key];
  const normalizedValue = normalizeSelectOptionValue(currentValue);
  if (!normalizedValue) return options;
  const hasCurrentValue = options.some((option) =>
    normalizeSelectOptionValue(option?.value) === normalizedValue,
  );
  if (hasCurrentValue) return options;
  return [
    ...options,
    {
      value: normalizedValue,
      label: `${normalizedValue} (legacy)`,
    },
  ];
};

const resolveFieldOptions = (field) => {
  if (typeof field?.optionsResolver === 'function') {
    const resolved = field.optionsResolver(model.value);
    return withLegacySelectOption(field, Array.isArray(resolved) ? resolved : []);
  }
  return withLegacySelectOption(field, Array.isArray(field?.options) ? field.options : []);
};

const isEngagementTypeField = (field) =>
  isSupplierTemplate.value && field?.key === 'rightToWorkEngagementType';
const resolveEngagementOptions = (field) => [
  { id: '', label: field.placeholder || 'Select an option' },
  ...resolveFieldOptions(field).map((option) => ({
    id: normalizeSelectOptionValue(option.value),
    label: option.label,
  })),
];

const isCheckboxTableGroupOption = (option) => option?.type === 'group';

const resolveCheckboxTableOptionKey = (field, option) => {
  if (isCheckboxTableGroupOption(option)) {
    return `${field?.key || 'checkbox-table'}-group-${option?.key || option?.label || ''}`;
  }
  return `${field?.key || 'checkbox-table'}-${option?.value || option?.label || ''}`;
};

const resolveCheckboxTablePrimaryColumnLabel = (field) =>
  field?.checkboxTablePrimaryColumnLabel || 'Document';

const resolveSelectValue = (field) => {
  if (field?.type !== 'select' || !field?.key) return '';
  const normalizedValue = normalizeSelectOptionValue(model.value?.[field.key]);
  if (!normalizedValue) return '';
  const options = resolveFieldOptions(field);
  const matchingOption = options.find((option) =>
    normalizeSelectOptionValue(option?.value) === normalizedValue,
  );
  return matchingOption ? normalizeSelectOptionValue(matchingOption?.value) : normalizedValue;
};

const resolveComputedText = (field) => {
  if (typeof field?.compute === 'function') {
    return field.compute(model.value);
  }
  if (!field?.key) return '';
  return model.value?.[field.key] ?? '';
};

const resolveFieldHint = (field) => typeof field?.hintResolver === 'function'
  ? field.hintResolver(model.value)
  : field?.hint;

const resolveDateFieldValue = (field) => {
  if (field?.type !== 'date' || !field?.key) return '';
  return model.value?.[field.key] ?? '';
};

const getCheckboxTableValue = (fieldKey) => {
  const current = model.value?.[fieldKey];
  if (!current || typeof current !== 'object' || Array.isArray(current)) {
    return {};
  }
  return current;
};

const getCheckboxTableOption = (fieldKey, optionValue) => {
  const current = getCheckboxTableValue(fieldKey)?.[optionValue];
  if (!current || typeof current !== 'object' || Array.isArray(current)) {
    return {};
  }
  return current;
};

const updateCheckboxTableField = (fieldKey, optionValue, prop, checked) => {
  const current = {
    ...getCheckboxTableValue(fieldKey),
  };
  const nextEntry = {
    seen: Boolean(getCheckboxTableOption(fieldKey, optionValue).seen),
    copied: Boolean(getCheckboxTableOption(fieldKey, optionValue).copied),
    [prop]: Boolean(checked),
  };

  if (!nextEntry.seen && !nextEntry.copied) {
    delete current[optionValue];
  } else {
    current[optionValue] = nextEntry;
  }

  updateField(fieldKey, current);
};

const setRelationPickerValue = (key, value) => {
  relationPickerValues.value = {
    ...relationPickerValues.value,
    [key]: value,
  };
};

const handleServiceGroupSelect = (key, value) => {
  setRelationPickerValue(key, null);
  if (!value) return;
  const current = Array.isArray(model.value[key]) ? [...model.value[key]] : [];
  const exists = current.some((id) => String(id) === String(value));
  if (!exists) {
    updateField(key, [...current, value]);
  }
};

const removeServiceGroup = (key, value) => {
  const current = Array.isArray(model.value[key]) ? model.value[key] : [];
  updateField(
    key,
    current.filter((id) => String(id) !== String(value)),
  );
};

const selectedServiceGroups = (field) => {
  const options = field.options || [];
  const ids = Array.isArray(model.value[field.key]) ? model.value[field.key] : [];
  return ids.map((id) => {
    const match = options.find((opt) => String(opt.id) === String(id));
    return {
      id,
      name: match?.name || `Group ${id}`,
    };
  });
};

const isSupplierPhoneField = (field) =>
  isSupplierTemplate.value && field?.key === 'telephone';

const fieldIsReadonly = (field) => Boolean(field?.readonly);

const focusField = async (fieldKey) => {
  await nextTick();
  const controls = Array.from(
    formElement.value?.querySelectorAll('input, textarea, select, button[role="combobox"]') || [],
  );
  const control = controls.find((element) => element.id === fieldKey);
  if (!control || control.disabled || control.readOnly) return false;
  control.focus();
  return true;
};

defineExpose({
  focusField,
});

// Only after the field is left, so typing an address is never interrupted.
const focusedFieldKey = ref('');
const emailFieldError = (field) => {
  if (field?.type !== 'email' || focusedFieldKey.value === field.key) return '';
  return isUnfinishedEmail(model.value?.[field.key])
    ? 'Not saved: enter a full email, e.g. name@example.com'
    : '';
};

const supplierPhoneValidationMessage = computed(() => {
  if (!isSupplierTemplate.value) return '';
  const raw = model.value?.telephone;
  const trimmed = String(raw ?? '').trim();
  if (!trimmed) return '';
  const normalized = normalizePhoneInput(trimmed);
  if (!normalized.startsWith('+')) {
    return 'Phone must start with + and country code';
  }
  if (!/^\+\d{6,15}$/.test(normalized)) {
    return 'Use international format, e.g. +447123456789';
  }
  return '';
});


watch(
  () => props.template,
  () => {
    // template change handled by parent through v-model
  }
);

watch(
  orderedBlocks,
  (blocks) => {
    const next = { ...collapsedSections.value };
    const activeSections = new Set();
    let isFirstSection = true;

    blocks.forEach((block) => {
      if (block.type !== 'section') return;
      const sectionId = block.sectionId;
      activeSections.add(sectionId);
      if (!(sectionId in next)) {
        next[sectionId] = !isFirstSection;
      }
      isFirstSection = false;
    });

    Object.keys(next).forEach((sectionId) => {
      if (!activeSections.has(sectionId)) {
        delete next[sectionId];
      }
    });

    collapsedSections.value = next;
  },
  { immediate: true },
);
</script>

<style scoped>
.catalog-form {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.catalog-form.catalog-table-form {
  gap: 0;
}

.catalog-table-form > .form-row {
  border-inline: 1px solid var(--border-color);
}

.catalog-table-form > .form-row:first-child,
.catalog-table-form > :not(.form-row) + .form-row {
  border-top: 1px solid var(--border-color);
}

.collapsible-section {
  border: 1px solid var(--border-color);
  border-radius: 8px;
  background: var(--noc-background);
}

.collapsible-toggle {
  width: fit-content;
  max-width: 100%;
  border: 0;
  background: transparent;
  color: var(--text-color);
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 8px;
  padding: 12px 14px;
  font-size: 1rem;
  font-weight: 600;
  text-align: left;
  cursor: pointer;
}

.collapsible-toggle:disabled {
  cursor: default;
}

.collapsible-label {
  position: relative;
}

.collapsible-label::after {
  content: '';
  position: absolute;
  left: 0;
  bottom: -7px;
  width: 20px;
  height: 2px;
  border-radius: 2px;
  background: var(--noc-blue, #0065ff);
  pointer-events: none;
  transform: scaleX(0);
  transform-origin: left center;
  transition: transform 200ms ease;
}

.collapsible-toggle[aria-expanded='true'] .collapsible-label::after {
  transform: scaleX(1);
}

.collapsible-indicator {
  width: 14px;
  height: 14px;
  flex-shrink: 0;
  stroke: currentColor;
  stroke-width: 0.75px;
  stroke-linejoin: round;
  transform: rotate(-90deg);
  transition: transform 0.15s ease;
}

.collapsible-indicator.open {
  transform: rotate(0deg);
}

@media (prefers-reduced-motion: reduce) {
  .collapsible-label::after,
  .collapsible-indicator {
    transition: none;
  }
}

.supplier-form .collapsible-content {
  padding-left: 20px;
  min-width: 0;
}

.collapsible-body {
  padding: 0 14px 14px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.form-row {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.form-row.columns {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 240px), 1fr));
  align-items: start;
}

.form-field {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.right-to-work-date-picker {
  display: flex;
  width: 100%;
  max-width: 100%;
  min-width: 0;
}

.right-to-work-date-picker :deep(.date-range-picker) {
  display: flex;
  width: 100%;
  min-width: 0;
}

.right-to-work-date-picker :deep(.drp-dropdown) {
  margin-top: -20px;
}

.right-to-work-date-picker :deep(.drp-trigger),
.right-to-work-date-picker :deep(.drp-manual-control) {
  width: 100%;
  box-sizing: border-box;
  min-height: var(--right-to-work-control-height);
  height: var(--right-to-work-control-height);
  justify-content: flex-start;
  padding: 0 12px;
  border: 1px solid var(--border-color);
  border-radius: var(--right-to-work-control-radius);
  background: var(--noc-background, #fff);
  font-size: var(--right-to-work-control-font-size);
  font-family: inherit;
  box-shadow: none;
  transition: border-color 0.1s ease, background-color 0.1s ease;
}

.right-to-work-date-picker :deep(.drp-manual-control) {
  padding: 0;
}

.right-to-work-date-picker :deep(.drp-trigger:hover),
.right-to-work-date-picker :deep(.drp-manual-control:hover),
.right-to-work-date-picker :deep(.drp-manual-control:focus-within) {
  border-color: var(--border-color-active);
  background: var(--noc-background, #fff);
}

.right-to-work-date-picker :deep(.drp-trigger--active),
.right-to-work-date-picker :deep(.drp-manual-control--active) {
  border-color: var(--border-color);
}

.right-to-work-date-picker :deep(.drp-trigger.drp-trigger--active:hover),
.right-to-work-date-picker :deep(.drp-manual-control--active:hover),
.right-to-work-date-picker :deep(.drp-manual-control--active:focus-within) {
  border-color: var(--border-color-active);
}

.right-to-work-date-picker :deep(.drp-manual-control--invalid),
.right-to-work-date-picker :deep(.drp-manual-control--invalid:focus-within) {
  border-color: var(--noc-red, #d9534f);
}

.right-to-work-date-picker :deep(.drp-trigger-text) {
  color: var(--text-color, #333);
  font-size: var(--right-to-work-control-font-size);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.right-to-work-date-picker :deep(.drp-manual-input) {
  padding: 0 12px;
  font-size: var(--right-to-work-control-font-size);
}

.supplier-form :deep(input:focus),
.supplier-form :deep(select:focus),
.supplier-form :deep(textarea:focus) {
  border-color: var(--border-color) !important;
  outline: none;
  box-shadow: none;
  background: var(--noc-background, #fff);
}

.supplier-form :deep(.drp-manual-input:focus),
.supplier-form :deep(.multi-input-field:focus) {
  border: 0 !important;
}

.supplier-form :deep(.field-input-invalid:focus) {
  border-color: var(--noc-red, #d9534f) !important;
}

.right-to-work-date-picker :deep(.drp-manual-toggle) {
  width: 42px;
  min-height: 34px;
}

.right-to-work-date-picker :deep(.drp-trigger-icon) {
  color: var(--text-color-secondary, #9ca3af);
  width: 16px;
  height: 16px;
  font-size: 16px;
}

.form-subsection-header {
  padding: 0 0 10px;
  border-bottom: 1px solid var(--border-color);
  background: transparent;
}

.form-subsection-title {
  margin: 0;
  color: var(--text-color);
  font: 600 14px/20px var(--font-family-base);
}

label {
  font-size: 0.85rem;
  color: var(--text-color-secondary);
}

input,
select,
textarea {
  border: 1px solid var(--border-color);
  border-radius: 4px;
  padding: 10px 12px;
  font-size: 0.9rem;
  background: var(--noc-background);
  color: var(--text-color);
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  max-height: 40px;
  transition: background-color 0.15s ease, border-color 0.15s ease;
}

.field-input-invalid {
  border-color: var(--noc-red, #d9534f);
}

.field-error {
  margin: 0;
  font-size: 0.8rem;
  color: var(--noc-red, #d9534f);
}

.field-hint {
  margin: 0;
  font-size: 0.8rem;
  color: var(--text-color-secondary);
}

input:hover,
select:hover,
textarea:hover {
  background: var(--noc-background-secondary);
}

textarea {
  min-height: 44px;
  max-height: none;
  overflow-y: hidden;
  resize: none;
}

.computed-field {
  min-height: 40px;
  border: 1px solid var(--border-color);
  border-radius: 4px;
  padding: 10px 12px;
  font-size: 0.9rem;
  background: var(--noc-background-secondary);
  color: var(--text-color);
  width: 100%;
  box-sizing: border-box;
  display: flex;
  align-items: center;
}

.checkbox-table-field {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.checkbox-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  border: 1px solid var(--border-color);
  border-radius: 0;
}

.checkbox-table th,
.checkbox-table td {
  border-bottom: 1px solid var(--border-color);
  padding: 10px 12px;
  font-size: 0.85rem;
  text-align: left;
}

.checkbox-table-group-row th {
  background: var(--noc-background-secondary);
  color: var(--text-color);
  font-weight: 700;
}

.checkbox-table tbody tr:last-child > :is(th, td) {
  border-bottom: 0;
}

.checkbox-cell {
  width: 72px;
  text-align: center !important;
}

.checkbox-cell input {
  width: 16px;
  height: 16px;
  padding: 0;
  margin: 0;
  max-height: none;
}

.attachments-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.hint {
  font-size: 0.8rem;
  color: var(--text-color-secondary);
}

.unsupported {
  font-size: 0.85rem;
  color: var(--noc-red);
}

.relation-field {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.relation-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.relation-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: var(--noc-background-secondary);
  border: 1px solid var(--border-color);
  border-radius: 999px;
  padding: 6px 10px;
  font-size: 0.85rem;
}

.chip-label {
  color: var(--text-color);
}

.chip-remove {
  background: none;
  border: none;
  cursor: pointer;
  color: var(--text-color-secondary);
  font-weight: 600;
  line-height: 1;
}

.chip-remove:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

:deep(.catalog-search-input .field-input) {
  min-height: 40px;
  height: 40px;
}

.catalog-multi-select :deep(.field-input) {
  border: 1px solid var(--border-color, #dcdcdc);
  background: var(--noc-background, #fff);
  padding: 6px;
  font-size: 0.9rem;
  width: 100%;
  height: auto;
  min-height: 40px;
  box-sizing: border-box;
  border-radius: 6px;
  transition: background-color 0.15s ease, border-color 0.15s ease;
}

.catalog-multi-select :deep(.field-input):hover {
  background: var(--noc-background-secondary, #fafafa);
}

.catalog-multi-select :deep(.field-input):focus {
  background: var(--noc-background, #fff);
  border-radius: 6px;
}

.catalog-info-table,
.additional-info-section {
  --right-to-work-control-height: 44px;
  --right-to-work-control-radius: 0;
  --right-to-work-control-font-size: 14px;
  border: 0;
  border-radius: 0;
  background: transparent;
}

.catalog-info-table .collapsible-toggle,
.additional-info-section .collapsible-toggle {
  min-height: 44px;
  padding: 0 0 12px;
}

.catalog-info-table .collapsible-body {
  padding: 0;
  gap: 0;
  border-top: 1px solid var(--border-color);
}

.general-info-table .collapsible-body,
.supplier-table-row {
  border-inline: 1px solid var(--border-color);
}

.right-to-work-section .collapsible-body {
  gap: 24px;
  border-top: 0;
}

.additional-info-section .collapsible-body {
  padding: 0;
  gap: 16px;
}

.supplier-table-row {
  border-top: 1px solid var(--border-color);
}

.form-row.catalog-info-table,
.catalog-info-table .form-row,
.form-row.supplier-table-row {
  display: block;
}

.form-row.supplier-table-row.columns {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  align-items: stretch;
  gap: 0;
}

.supplier-table-row.columns > .form-field + .form-field {
  border-left: 1px solid var(--border-color);
}

.catalog-info-table .form-field,
.supplier-table-row .form-field {
  display: grid;
  grid-template-columns: minmax(0, 38%) minmax(0, 1fr);
  gap: 0;
  min-height: 44px;
  border-bottom: 1px solid var(--border-color);
  background: var(--noc-background, #fff);
}

.catalog-table-form > .form-row:has(+ .form-row) > .form-field,
.general-info-table .form-row:not(:last-child) > .form-field,
.catalog-info-table .form-field:not(:last-child) {
  border-image: linear-gradient(to right, transparent 16px, var(--border-color) 16px) 1;
}

.supplier-form .right-to-work-section .form-field {
  border-inline: 1px solid var(--border-color);
  border-image: none;
}

.catalog-info-table .form-field > label,
.supplier-table-row .form-field > label {
  grid-column: 1;
  grid-row: 1;
  display: flex;
  align-items: center;
  padding: 10px 12px;
  border-right: 1px solid var(--border-color);
  background: var(--noc-background-secondary, #fafafb);
  font-size: 14px;
  line-height: 20px;
  overflow-wrap: anywhere;
}

.catalog-info-table .form-field:has(> .field-hint, > .field-error) > label {
  grid-row: 1 / span 2;
}

.catalog-info-table .form-field > :not(label),
.supplier-table-row .form-field > :not(label) {
  grid-column: 2;
  min-width: 0;
}

.catalog-info-table .form-field > :is(input, select, textarea, .computed-field),
.supplier-table-row .form-field > input,
.catalog-info-table :deep(.catalog-search-input .field-input),
.catalog-info-table :deep(.drp-manual-control),
.catalog-info-table :deep(.drp-trigger) {
  width: 100%;
  min-height: 44px;
  height: 100%;
  max-height: none;
  padding: 10px 12px;
  border: 0;
  border-radius: 0;
  outline: none;
  box-shadow: none;
  background: var(--noc-background, #fff);
  font: 400 14px/20px var(--font-family-base);
  box-sizing: border-box;
  transition: none;
}

.catalog-info-table .form-field > :is(input, textarea),
.supplier-table-row .form-field > input {
  cursor: text;
}

.catalog-info-table .form-field > select {
  cursor: pointer;
}

.engagement-type-field {
  position: relative;
  min-width: 0;
}

.engagement-type-field :deep(.catalog-search-input) {
  height: 100%;
}

.engagement-type-field :deep(.catalog-search-input .select-trigger) {
  padding-right: 36px;
}

.engagement-type-chevron {
  position: absolute;
  top: 50%;
  right: 12px;
  transform: translateY(-50%);
  color: var(--text-color-secondary);
}

.engagement-type-field :deep(.dropdown-menu) {
  margin: 0;
}

.engagement-type-field :deep([role='option']) {
  display: flex;
  align-items: center;
  min-height: 40px;
  padding: 8px 12px;
  font: 400 14px/20px var(--font-family-base);
  touch-action: manipulation;
}

.catalog-table-form .relation-field {
  gap: 0;
}

.catalog-table-form .relation-tags {
  padding: 0 12px 10px;
}

.catalog-info-table :deep(.catalog-search-input.is-multiple .field-input) {
  height: auto;
  min-height: 100%;
}

.catalog-info-table :deep(.multi-input-field) {
  min-width: min(90px, 100%);
  max-width: 100%;
  outline: none;
}

.catalog-info-table :deep(.multi-chip),
.catalog-table-form .relation-chip {
  min-width: 0;
  max-width: 100%;
  box-sizing: border-box;
  white-space: normal;
}

.catalog-info-table :deep(.chip-label) {
  min-width: 0;
  overflow-wrap: anywhere;
}

.catalog-info-table :deep(.chip-remove) {
  flex-shrink: 0;
}

.catalog-table-form :deep(.attachment-manager) {
  padding: 12px;
}

.catalog-table-form :deep(.upload-zone),
.catalog-table-form :deep(.attachment-row) {
  border-radius: 0;
}

.catalog-table-form :deep(.attachment-row) {
  flex-wrap: wrap;
}

.catalog-table-form :deep(.attachment-meta) {
  min-width: 0;
  overflow-wrap: anywhere;
}

.catalog-info-table .form-field > :is(.field-hint, .field-error) {
  padding: 0 12px 8px;
}

.catalog-info-table :deep(.drp-manual-control) {
  padding: 0;
}

.catalog-info-table :deep(.drp-manual-toggle) {
  min-height: 44px;
  width: 42px;
}

.catalog-info-table :deep(.drp-manual-input) {
  min-height: 44px;
  padding-left: 12px;
  font-size: 14px;
}

.catalog-info-table .form-field > :is(input, select, textarea):hover,
.supplier-table-row .form-field > input:hover,
.catalog-info-table :deep(.field-input:hover),
.catalog-info-table :deep(.drp-manual-control:hover) {
  background: var(--noc-background-secondary, #fafafb);
}

.catalog-info-table .form-field > :is(input, select, textarea):focus,
.supplier-table-row .form-field > input:focus,
.catalog-info-table :deep(.field-input:focus-within),
.catalog-info-table :deep(.drp-manual-input:focus),
.catalog-info-table :deep(.drp-manual-control:focus-within) {
  border: 0 !important;
  outline: none;
  box-shadow: none;
  background: var(--noc-background, #fff);
}

.catalog-info-table .field-input-invalid,
.catalog-info-table :deep(.drp-manual-control--invalid) {
  box-shadow: inset 0 -1px var(--noc-red, #d9534f) !important;
}

</style>
